import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useSearchParams,
} from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "@/lib/tanstack-query/client";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { ScrollToTop } from "@/components/ScrollToTop";
import Layout from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthenticatedGuard } from "./components/ui/AuthenticatedGuard";
import { PageLoader } from "./components/PageLoader";

// @ts-expect-error: BigInt.prototype.toJSON is not a function in typescript
BigInt.prototype.toJSON = function () {
  return String(this);
};

function VerifyTokenRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={`/auth/continue${qs ? `?${qs}` : ""}`} replace />;
}

// Lazy-loaded pages (lazyWithRetry auto-reloads on stale chunk errors after deploys)
const HomePage = lazyWithRetry(() => import("./pages/HomePage"));
const DiscoverPage = lazyWithRetry(() => import("./pages/DiscoverPage"));
const PersonalizePage = lazyWithRetry(() => import("./pages/PersonalizePage"));
const SharedPersonalizationPage = lazyWithRetry(
  () => import("./pages/SharedPersonalizationPage"),
);
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const SignupPage = lazyWithRetry(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = lazyWithRetry(
  () => import("./pages/ResetPasswordPage"),
);
const AuthContinuePage = lazyWithRetry(
  () => import("./pages/AuthContinuePage"),
);
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage"));
const EditProfilePage = lazyWithRetry(() =>
  import("./pages/EditProfilePage").then((m) => ({
    default: m.EditProfilePage,
  })),
);
const SettingsPage = lazyWithRetry(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const InboxPage = lazyWithRetry(() => import("./pages/InboxPage"));
const PeoplePage = lazyWithRetry(() =>
  import("./pages/PeoplePage").then((m) => ({ default: m.PeoplePage })),
);
const ProjectsPage = lazyWithRetry(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazyWithRetry(
  () => import("./pages/ProjectDetailPage"),
);
const EditProjectPage = lazyWithRetry(() =>
  import("./pages/EditProjectPage").then((m) => ({
    default: m.EditProjectPage,
  })),
);
const PortfoliosPage = lazyWithRetry(() => import("./pages/PortfoliosPage"));
const PortfolioDetailPage = lazyWithRetry(
  () => import("./pages/PortfolioDetailPage"),
);
const CuratePortfolioPage = lazyWithRetry(() =>
  import("./pages/CuratePortfolioPage").then((m) => ({
    default: m.CuratePortfolioPage,
  })),
);
const EditPortfolioPage = lazyWithRetry(() =>
  import("./pages/EditPortfolioPage").then((m) => ({
    default: m.EditPortfolioPage,
  })),
);
const FundingPoolsPage = lazyWithRetry(() => import("./pages/ChallengesPage"));
const ChallengeDetailPage = lazyWithRetry(
  () => import("./pages/ChallengeDetailPage"),
);
const EditChallengePage = lazyWithRetry(() =>
  import("./pages/EditChallengePage").then((m) => ({
    default: m.EditChallengePage,
  })),
);
const TeamDashboard = lazyWithRetry(() =>
  import("./pages/TeamDashboard").then((m) => ({ default: m.TeamDashboard })),
);
const ConfirmPaymentPage = lazyWithRetry(
  () => import("./pages/ConfirmPaymentPage"),
);
const PortfolioSettlementReauthPage = lazyWithRetry(
  () => import("./pages/PortfolioSettlementReauthPage"),
);
const PortfolioSettlementRetryPage = lazyWithRetry(
  () => import("./pages/PortfolioSettlementRetryPage"),
);
const ProfilePage = lazyWithRetry(() =>
  import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"));
const CareersPage = lazyWithRetry(() => import("./pages/CareersPage"));
const CareerPage = lazyWithRetry(() => import("./pages/CareerPage"));
const NsfPilotPage = lazyWithRetry(() => import("./pages/NsfPilotPage"));
const FAQPage = lazyWithRetry(() => import("./pages/FAQPage"));
const PrivacyPolicyPage = lazyWithRetry(
  () => import("./pages/PrivacyPolicyPage"),
);
const TermsOfServicePage = lazyWithRetry(
  () => import("./pages/TermsOfServicePage"),
);
const ProjectPreviewPage = lazyWithRetry(() =>
  import("./pages/ProjectPreviewPage").then((m) => ({
    default: m.ProjectPreviewPage,
  })),
);
const PortfolioPreviewPage = lazyWithRetry(() =>
  import("./pages/PortfolioPreviewPage").then((m) => ({
    default: m.PortfolioPreviewPage,
  })),
);
const ChallengePreviewPage = lazyWithRetry(() =>
  import("./pages/ChallengePreviewPage").then((m) => ({
    default: m.ChallengePreviewPage,
  })),
);
const PostDetailPage = lazyWithRetry(() => import("./pages/PostDetailPage"));
const StripeCallbackPage = lazyWithRetry(
  () => import("./pages/StripeCallbackPage"),
);
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <QueryClientProvider client={queryClient}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <SentryRoutes>
                <Route path="/" element={<HomePage />} />

                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/continue" element={<AuthContinuePage />} />
                <Route path="/verify-token" element={<VerifyTokenRedirect />} />

                {/* requireOnboarding={false}: the onboarding page must never
                    redirect to itself */}
                <Route
                  element={<AuthenticatedGuard requireOnboarding={false} />}
                >
                  <Route path="/onboarding" element={<OnboardingPage />} />
                </Route>

                <Route element={<AuthenticatedGuard />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profile" element={<EditProfilePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route
                    path="/inbox/:conversationId"
                    element={<InboxPage />}
                  />
                </Route>

                <Route path="/discover" element={<DiscoverPage />} />
                {/* Public: no auth guard — a draft is not a session. */}
                <Route path="/personalize" element={<PersonalizePage />} />
                {/* Public snapshot: the link is the credential. */}
                <Route
                  path="/p/:slug"
                  element={<SharedPersonalizationPage />}
                />

                <Route path="/people" element={<PeoplePage />} />

                <Route path="/projects" element={<ProjectsPage />} />
                <Route
                  path="/projects/:idOrSlug"
                  element={<ProjectDetailPage />}
                />
                <Route
                  path="/projects/:idOrSlug/posts/:postIdOrSlug"
                  element={<PostDetailPage />}
                />
                <Route element={<AuthenticatedGuard />}>
                  <Route
                    path="/projects/:idOrSlug/edit"
                    element={<EditProjectPage />}
                  />
                  <Route
                    path="/projects/:idOrSlug/preview"
                    element={<ProjectPreviewPage />}
                  />
                </Route>

                <Route path="/portfolios" element={<PortfoliosPage />} />
                <Route
                  path="/portfolios/:idOrSlug"
                  element={<PortfolioDetailPage />}
                />
                <Route
                  path="/portfolios/:idOrSlug/posts/:postIdOrSlug"
                  element={<PostDetailPage />}
                />
                <Route element={<AuthenticatedGuard />}>
                  <Route
                    path="/portfolios/:idOrSlug/curate"
                    element={<CuratePortfolioPage />}
                  />
                  <Route
                    path="/portfolios/:idOrSlug/edit"
                    element={<EditPortfolioPage />}
                  />
                  <Route
                    path="/portfolios/:idOrSlug/preview"
                    element={<PortfolioPreviewPage />}
                  />
                </Route>

                <Route path="/funding-pools" element={<FundingPoolsPage />} />
                <Route
                  path="/funding-pools/:idOrSlug"
                  element={<ChallengeDetailPage />}
                />
                <Route
                  path="/funding-pools/:idOrSlug/posts/:postIdOrSlug"
                  element={<PostDetailPage />}
                />
                <Route element={<AuthenticatedGuard />}>
                  <Route
                    path="/funding-pools/:idOrSlug/edit"
                    element={<EditChallengePage />}
                  />
                  <Route
                    path="/funding-pools/:idOrSlug/preview"
                    element={<ChallengePreviewPage />}
                  />
                </Route>

                <Route element={<AuthenticatedGuard />}>
                  <Route
                    path="/teams/:id/dashboard"
                    element={<TeamDashboard />}
                  />
                </Route>

                <Route element={<AuthenticatedGuard />}>
                  <Route
                    path="/confirm-payment"
                    element={<ConfirmPaymentPage />}
                  />
                  <Route
                    path="/portfolio-settlement-reauth"
                    element={<PortfolioSettlementReauthPage />}
                  />
                  <Route
                    path="/portfolio-settlement-retry"
                    element={<PortfolioSettlementRetryPage />}
                  />
                </Route>

                <Route path="/profile/:idOrHandle" element={<ProfilePage />} />

                <Route
                  path="/callbacks/stripe"
                  element={<StripeCallbackPage />}
                />

                <Route path="/about" element={<AboutPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/careers/:slug" element={<CareerPage />} />
                <Route path="/nsf-pilot" element={<NsfPilotPage />} />
                {/* Outreach resume link: the same page, opened mid-flow from a
                    pre-filled record. Public, like the walk-up form. */}
                <Route path="/nsf-pilot/r/:token" element={<NsfPilotPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />

                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
            </Suspense>
          </Layout>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
